import { useMemo } from 'react';
import { Box, Button, Text, Tree } from '@mantine/core';
import type { RenderTreeNodePayload, TreeNodeData, UseTreeReturnType } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import type { Company, Plant } from '../../types/entities';
import { effectiveActiveKilns, plantClosureLabel } from './kilnActivity';
import classes from './MasterDataTree.module.css';

const COMPANY_PREFIX = 'company-';
const PLANT_PREFIX = 'plant-';

export function companyNodeValue(companyId: number): string {
  return `${COMPANY_PREFIX}${companyId}`;
}

export function plantNodeValue(plantId: number): string {
  return `${PLANT_PREFIX}${plantId}`;
}

export type MasterDataNodeSelection = { type: 'company'; id: number } | { type: 'plant'; id: number };

export function decodeMasterDataNode(value: string | undefined): MasterDataNodeSelection | null {
  if (!value) {
    return null;
  }
  if (value.startsWith(COMPANY_PREFIX)) {
    return { type: 'company', id: Number(value.slice(COMPANY_PREFIX.length)) };
  }
  if (value.startsWith(PLANT_PREFIX)) {
    return { type: 'plant', id: Number(value.slice(PLANT_PREFIX.length)) };
  }
  return null;
}

function pluralOfen(count: number): string {
  return count === 1 ? 'Ofen' : 'Öfen';
}

interface MasterDataTreeProps {
  companies: Company[];
  plants: Plant[];
  tree: UseTreeReturnType;
  onSelectCompany: (companyId: number) => void;
  onSelectPlant: (plantId: number, companyId: number) => void;
  onCreateCompany: () => void;
}

/**
 * Baum links (UI-SPEC "Struktur-Klarstellung": ZWEI Ebenen, Unternehmen -> Werk, Oefen sind kein
 * eigener Knoten). Ueber Mantine `Tree`/`useTree` (Tastaturnavigation/ARIA/roving-tabindex bereits
 * eingebaut, 28-RESEARCH.md Pattern 5) statt eines selbstgebauten `Set`-Aufklappens wie im Sketch.
 *
 * Zwei getrennte Klickziele je Unternehmenszeile (D-03/UI-SPEC Punkt 9): die ganze Zeile waehlt
 * aus UND klappt auf (ein weiterer Klick auf die bereits gewaehlte, offene Zeile klappt zu), das
 * darueberliegende Dreieck klappt nur auf/zu, ohne die Auswahl anzufassen. Mantines eingebautes
 * `handleNodeClick` (gesteuert ueber `expandOnClick`/`selectOnClick`) kann diese Trennung nicht
 * abbilden -- deshalb bewusst `expandOnClick={false}`/`selectOnClick={false}` am Tree und eigene
 * Klick-Handler in `renderNode`, statt `elementProps.onClick` zu verwenden.
 *
 * Mantine behandelt bislang nur Pfeiltasten/Space in seinem internen `handleKeyDown` der Baumzeile
 * -- Enter loest dort nichts aus. Der Enter-Handler sitzt deshalb einmal am Container und liest den
 * Knotenwert vom fokussierten `[role="treeitem"]` ab (Event-Delegation), statt ihn an jeder Zeile
 * zu duplizieren.
 */
export function MasterDataTree({
  companies,
  plants,
  tree,
  onSelectCompany,
  onSelectPlant,
  onCreateCompany,
}: MasterDataTreeProps) {
  const plantsByCompanyId = useMemo(() => {
    const map = new Map<number, Plant[]>();
    for (const plant of plants) {
      if (!plant.company) {
        continue;
      }
      const list = map.get(plant.company.id) ?? [];
      list.push(plant);
      map.set(plant.company.id, list);
    }
    return map;
  }, [plants]);

  const treeData: TreeNodeData[] = useMemo(
    () =>
      companies.map((company) => {
        const companyPlants = plantsByCompanyId.get(company.id) ?? [];
        return {
          value: companyNodeValue(company.id),
          label: company.name,
          // Kein Twist-Icon fuer Unternehmen ohne Werk: Array.isArray([]) waere true und
          // Mantine wuerde faelschlich einen Chevron fuer "leere" Kinder anzeigen.
          children:
            companyPlants.length > 0
              ? companyPlants.map((plant) => ({ value: plantNodeValue(plant.id), label: plant.name }))
              : undefined,
        };
      }),
    [companies, plantsByCompanyId]
  );

  function renderNode({ node, expanded, selected, tree: controller }: RenderTreeNodePayload) {
    if (node.value.startsWith(COMPANY_PREFIX)) {
      const companyId = Number(node.value.slice(COMPANY_PREFIX.length));
      const company = companies.find((c) => c.id === companyId);
      if (!company) {
        return null;
      }
      const companyPlants = plantsByCompanyId.get(companyId) ?? [];
      return (
        <div
          className={selected ? `${classes.companyRow} ${classes.selected}` : classes.companyRow}
          onClick={() => {
            // Zweiter Klick auf das bereits ausgewaehlte, offene Unternehmen klappt die Werke
            // wieder zu (Auswahl bleibt bestehen -- das Detail rechts wechselt dabei nicht).
            if (selected && expanded) {
              controller.collapse(node.value);
              return;
            }
            onSelectCompany(companyId);
          }}
        >
          {companyPlants.length === 0 ? (
            <span className={classes.chevronSpacer} aria-hidden="true" />
          ) : (
            <button
              type="button"
              className={classes.chevron}
              tabIndex={-1}
              aria-label={expanded ? 'Unternehmen zuklappen' : 'Unternehmen aufklappen'}
              onClick={(event) => {
                // Liegt ueber der Zeile: klappt nur auf/zu, ohne die Auswahl zu aendern.
                event.stopPropagation();
                controller.toggleExpanded(node.value);
              }}
            >
              <IconChevronDown
                size={14}
                className={expanded ? classes.chevronIcon : `${classes.chevronIcon} ${classes.chevronClosed}`}
              />
            </button>
          )}
          <span className={classes.companyName}>{company.name}</span>
        </div>
      );
    }

    const plantId = Number(node.value.slice(PLANT_PREFIX.length));
    const plant = plants.find((p) => p.id === plantId);
    if (!plant || !plant.company) {
      return null;
    }
    const companyId = plant.company.id;
    // D-07/D-12: ein stillgelegtes Werk zeigt rechts den Kennzeichnungstext statt der Ofenzahl --
    // auch bei einem kuenftigen Datum (Sketch 027 `treeMark`, Variante D). Ohne Datum bleibt es
    // bei der bisherigen, nur heute effektiv aktiven Ofenzahl (D-11).
    const closure = plantClosureLabel(plant);
    const kilnCount = effectiveActiveKilns(plant).length;
    return (
      <div
        className={selected ? `${classes.plantRow} ${classes.selected}` : classes.plantRow}
        onClick={() => onSelectPlant(plantId, companyId)}
      >
        <span>{plant.name}</span>
        {/* Werke ohne Ofen bleiben rechts leer: die Oefen kommen mit dem Werk aus derselben
            Antwort, leer heisst hier also eindeutig "keine" -- eine ausgeschriebene 0 in der
            Mehrheit der Zeilen wuerde nur die wenigen Zeilen mit Oefen zudecken. */}
        {closure ? (
          <span className={classes.closure}>{closure}</span>
        ) : (
          kilnCount > 0 && (
            <span className={classes.count}>{`${kilnCount} ${pluralOfen(kilnCount)}`}</span>
          )
        )}
      </div>
    );
  }

  function handleTreeKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key !== 'Enter') {
      return;
    }
    const target = event.target as HTMLElement;
    const value = target.closest('[role="treeitem"]')?.getAttribute('data-value');
    if (!value) {
      return;
    }
    if (value.startsWith(COMPANY_PREFIX)) {
      event.preventDefault();
      onSelectCompany(Number(value.slice(COMPANY_PREFIX.length)));
      return;
    }
    if (value.startsWith(PLANT_PREFIX)) {
      const plantId = Number(value.slice(PLANT_PREFIX.length));
      const plant = plants.find((p) => p.id === plantId);
      if (plant?.company) {
        event.preventDefault();
        onSelectPlant(plantId, plant.company.id);
      }
    }
  }

  if (companies.length === 0) {
    return (
      <Box className={classes.emptyState}>
        <Text size="sm" c="vdzGray.6" mb="sm">
          Noch keine Unternehmen angelegt.
        </Text>
        <Button size="xs" onClick={onCreateCompany}>
          Unternehmen anlegen
        </Button>
      </Box>
    );
  }

  return (
    <div onKeyDown={handleTreeKeyDown}>
      <Tree
        data={treeData}
        tree={tree}
        renderNode={renderNode}
        expandOnClick={false}
        selectOnClick={false}
        aria-label="Unternehmen und Werke"
      />
    </div>
  );
}
